import { ActionIcon, Group, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { type TaskType, useDeleteTaskType, useUpdateTaskType } from 'modules/Tasks/api';
import { TaskTypeForm } from 'modules/Tasks/TaskTypeForm/TaskTypeForm';

type TaskTypeListItemProps = {
  item: TaskType;
  refetch: () => void;
};

export const TaskTypeListItem = ({ item, refetch }: TaskTypeListItemProps) => {
  const { t } = useTranslation();

  const { deleteTaskType } = useDeleteTaskType();
  const { updateTaskType } = useUpdateTaskType();

  const { id, name, isDeletable } = item;

  const handleUpdate = () =>
    modals.open({
      size: 'sm',
      title: t(i18nKeys.EDIT_TASK_TYPE),
      children: (
        <TaskTypeForm
          taskType={item}
          onCancel={() => modals.closeAll()}
          onSubmit={data =>
            updateTaskType(
              { id, data },
              {
                onSuccess: () => {
                  refetch();
                  modals.closeAll();
                },
              },
            )
          }
        />
      ),
    });

  const handleDelete = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.CONFIRMATION),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.AYS.DELETE_TASK_TYPE)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteTaskType({ id }, { onSuccess: () => refetch() }),
    });

  return (
    <Table.Tr>
      <Table.Td>{id}</Table.Td>
      <Table.Td>{name}</Table.Td>
      <Table.Td>
        {t(
          !isDeletable ? i18nKeys.SETTINGS.TASKS.TASK_DEFAULT : i18nKeys.SETTINGS.TASKS.TASK_CUSTOM,
        )}
      </Table.Td>
      <Table.Td>
        {isDeletable && (
          <Group gap="xs" wrap="nowrap">
            <ActionIcon variant="subtle" aria-label={t(i18nKeys.EDIT)} onClick={handleUpdate}>
              <IconPencil size={18} stroke={1.5} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={t(i18nKeys.DELETE)}
              onClick={handleDelete}
            >
              <IconTrash size={18} stroke={1.5} />
            </ActionIcon>
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  );
};
